import type { LscArchiveItem } from './types';

export type ArchivePlaybackFilterMode = 'all' | 'playable' | 'unavailable';

export type ArchivePlaybackFields = Pick<
  LscArchiveItem,
  'playback_status' | 'playback_unavailable_reason'
>;

export function isArchivePlaybackUnavailable(archive: ArchivePlaybackFields): boolean {
  return String(archive.playback_status ?? '').trim().toLowerCase() === 'unavailable';
}

export function isArchivePlaybackPlayable(archive: ArchivePlaybackFields): boolean {
  return !isArchivePlaybackUnavailable(archive);
}

export function getArchivePlaybackLabel(archive: ArchivePlaybackFields): string {
  const status = String(archive.playback_status ?? '').trim().toLowerCase();
  if (status === 'available') return 'Playable';
  if (status === 'unavailable') return 'Unavailable on Twitch';
  return 'Playback not checked';
}

export function countUnavailableArchivePlayback<T extends ArchivePlaybackFields>(
  archives: T[]
): number {
  return archives.filter(isArchivePlaybackUnavailable).length;
}

export function filterArchivesByPlayback<T extends ArchivePlaybackFields>(
  archives: T[],
  mode: ArchivePlaybackFilterMode
): T[] {
  if (mode === 'all') return archives;
  if (mode === 'unavailable') {
    return archives.filter(isArchivePlaybackUnavailable);
  }
  return archives.filter(isArchivePlaybackPlayable);
}

export function selectLatestPlayableArchive<T extends ArchivePlaybackFields & { published_at?: string }>(
  archives: T[]
): T | null {
  const playable = archives.filter(isArchivePlaybackPlayable);
  const pool = playable.length > 0 ? playable : archives;
  return [...pool].sort((left, right) => {
    const leftTime = left.published_at ? Date.parse(left.published_at) : 0;
    const rightTime = right.published_at ? Date.parse(right.published_at) : 0;
    return rightTime - leftTime;
  })[0] ?? null;
}
